import React, { useState, useEffect } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Tabs } from 'devextreme-react/tabs';
import { DataGrid, Column, Paging } from 'devextreme-react/data-grid';
import { FileUploader } from 'devextreme-react/file-uploader';
import { Button } from 'devextreme-react/button';
import { Autocomplete } from 'devextreme-react/autocomplete';
import { TextBox } from 'devextreme-react/text-box';
import { NumberBox } from 'devextreme-react/number-box';
import { DateBox } from 'devextreme-react/date-box';
import { CheckBox } from 'devextreme-react/check-box';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { graveSitesApi, deceasedApi, cemeteryApi } from '../services/api';
import type { CemeteryData } from '../types';
import DistortableImageLayer from './DistortableImageLayer';
import { getOverlayPlacement } from '../lib/cemeteryOverlay';

// Fix for default marker icons in React Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Cemetery bounds for Pokopališče Zagorje ob Savi
const CEMETERY_BOUNDS = {
  name: "Pokopališče Zagorje ob Savi",
  southwest: [46.133992, 14.997715] as [number, number],
  northeast: [46.135867, 14.999685] as [number, number],
  center: [46.1349295, 14.9987] as [number, number],
  zoom: 19
};



interface GraveSitePopupEditorProps {
  visible: boolean;
  data: any;
  isNew: boolean;
  cemeterySlug?: string;
  categories: string[];
  onHiding: () => void;
  onSaved: () => void;
}

const GraveSitePopupEditor: React.FC<GraveSitePopupEditorProps> = ({
  visible,
  data,
  isNew,
  cemeterySlug,
  categories,
  onHiding,
  onSaved,
}) => {
  const [formData, setFormData] = useState<any>({});
  
  // Debug wrapper for setFormData to track changes
  const updateFormData = (newData: any) => {
    // Guard: Don't update formData when popup is not visible (prevents DevExtreme cleanup corruption)
    if (!visible) {
      return;
    }
    
    if (typeof newData === 'function') {
      setFormData((prev: any) => {
        const result = newData(prev);
        return result;
      });
    } else {
      setFormData(newData);
    }
  };
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [cemeteryData, setCemeteryData] = useState<CemeteryData | null>(null);
  const [locationMarker, setLocationMarker] = useState<{ lat: number; lng: number } | null>(null);
  const [currentPhoto, setCurrentPhoto] = useState<string | null>(null);
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [initialGraveRow, setInitialGraveRow] = useState<number | null>(null);
  
  // Autocomplete data sources
  const [firstNames, setFirstNames] = useState<string[]>([]);
  const [lastNames, setLastNames] = useState<string[]>([]);
  const [middleNames, setMiddleNames] = useState<string[]>([]);
  const [maidenNames, setMaidenNames] = useState<string[]>([]);
  const [graveTypes, setGraveTypes] = useState<string[]>([]);
  
  // Deceased person popup states
  const [deceasedPopupVisible, setDeceasedPopupVisible] = useState(false);
  const [deceasedFormData, setDeceasedFormData] = useState<any>({});
  const [editingDeceasedId, setEditingDeceasedId] = useState<number | null>(null);
  const [isSavingDeceased, setIsSavingDeceased] = useState(false);

  // Initialize default deceased form data
  const getDefaultDeceasedFormData = () => ({
    graveSiteId: data?.id,
    allowPublication: true,
    firstName: '',
    lastName: '',
    middleName: '',
    maidenName: '',
    birthDate: null,
    deathDate: null,
    burialDate: null,
    graveType: '',
    birthYear: null,
    deathYear: null,
    contractNumber: '',
    notes: ''
  });

  // Reset deceased form when popup visibility changes
  useEffect(() => {
    if (!deceasedPopupVisible) {
      // Clean up form when popup is closed - reset to completely empty state
      setDeceasedFormData({
        graveSiteId: data?.id,
        allowPublication: true,
        firstName: '',
        lastName: '',
        middleName: '',
        maidenName: '',
        birthDate: null,
        deathDate: null,
        burialDate: null,
        graveType: '',
        birthYear: null,
        deathYear: null,
        contractNumber: '',
        notes: ''
      });
      setEditingDeceasedId(null);
    }
  }, [deceasedPopupVisible, data?.id]);

  // Initialize form data when popup opens
  useEffect(() => {
    if (visible && data) {
      // Use setFormData directly to ensure data is set even if guard blocks updateFormData
      setFormData({ ...data });
      setInitialGraveRow(data.graveRow || null);
      
      // Set location marker if coordinates exist
      if (data.latitude !== null && data.longitude !== null && data.latitude !== undefined && data.longitude !== undefined) {
        setLocationMarker({ lat: data.latitude, lng: data.longitude });
      } else {
        setLocationMarker(null);
      }
      
      // Set current photo if exists
      if (data.photoPath && data.photoPath.trim() !== '') {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068';
        const photoUrl = `${baseUrl.replace('/api', '')}${data.photoPath}`;
        setCurrentPhoto(photoUrl);
      } else {
        setCurrentPhoto(null);
      }
    }

    // Reset states when popup closes - use setFormData directly to bypass guard
    if (!visible) {
      setFormData({});
      setLocationMarker(null);
      setCurrentPhoto(null);
      setSelectedTabIndex(0);
      setInitialGraveRow(null);
    }
  }, [visible, data]);

  // Load cemetery data for location tab
  useEffect(() => {
    if (selectedTabIndex === 2) {
      loadCemeteryData();
    }
  }, [selectedTabIndex, cemeterySlug]);

  // Load autocomplete data sources on component mount
  useEffect(() => {
    const loadAutocompleteData = async () => {
      try {
        const [firstNamesData, lastNamesData, middleNamesData, maidenNamesData, graveTypesData] = await Promise.all([
          deceasedApi.getFirstNames(cemeterySlug),
          deceasedApi.getLastNames(cemeterySlug),
          deceasedApi.getMiddleNames(cemeterySlug),
          deceasedApi.getMaidenNames(cemeterySlug),
          deceasedApi.getGraveTypes(cemeterySlug)
        ]);
        
        setFirstNames(firstNamesData);
        setLastNames(lastNamesData);
        setMiddleNames(middleNamesData);
        setMaidenNames(maidenNamesData);
        setGraveTypes(graveTypesData);
      } catch (error) {
        console.error('Failed to load autocomplete data:', error);
      }
    };

    loadAutocompleteData();
  }, [cemeterySlug]);

  const loadCemeteryData = async () => {
    try {
      const data = await cemeteryApi.getCemeteryData(cemeterySlug);
      setCemeteryData(data);
    } catch (error) {
      console.error('Failed to load cemetery data:', error);
      notify('Napaka pri nalaganju podatkov pokopališča', 'error', 3000);
    }
  };

  const handleSaveLocation = async () => {
    if (!data.id) {
      notify('Najprej shranite osnovne podatke', 'warning', 3000);
      return;
    }

    if (!locationMarker) {
      notify('Najprej nastavite lokacijo na zemljevidu', 'warning', 3000);
      return;
    }

    setIsSavingLocation(true);
    try {
      await graveSitesApi.updateGeoLocation(
        data.id,
        locationMarker.lat,
        locationMarker.lng,
        cemeterySlug
      );
      notify('Lokacija shranjena', 'success', 2000);
      onSaved();
    } catch (error) {
      console.error('Failed to save location:', error);
      notify('Napaka pri shranjevanju lokacije', 'error', 3000);
    } finally {
      setIsSavingLocation(false);
    }
  };

  const handleClearLocation = () => {
    setLocationMarker(null);
  };

  // Component to handle map clicks for setting marker location
  const MapClickHandler: React.FC<{ onLocationSet: (lat: number, lng: number) => void }> = ({ onLocationSet }) => {
    useMapEvents({
      click(e) {
        onLocationSet(e.latlng.lat, e.latlng.lng);
      },
    });
    return null;
  };

  const handlePhotoUpload = async (e: any) => {
    if (!data?.id) {
      notify('Najprej shranite osnovne podatke', 'warning', 3000);
      return;
    }

    // Get files from DevExtreme FileUploader
    const files = e.value;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    if (!file) return;

    setIsUploadingPhoto(true);
    try {
      const result = await graveSitesApi.uploadPhoto(data.id, file, cemeterySlug);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068';
      setCurrentPhoto(`${baseUrl.replace('/api', '')}${result.photoPath}`);
      notify('Fotografija naložena', 'success', 2000);
      onSaved();
    } catch (error: any) {
      console.error('Failed to upload photo:', error);
      const errorMsg = error.response?.data?.error || 'Napaka pri nalaganju fotografije';
      notify(errorMsg, 'error', 3000);
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!data.id || !currentPhoto) return;

    try {
      await graveSitesApi.deletePhoto(data.id, cemeterySlug);
      setCurrentPhoto(null);
      notify('Fotografija izbrisana', 'success', 2000);
      onSaved();
    } catch (error) {
      console.error('Failed to delete photo:', error);
      notify('Napaka pri brisanju fotografije', 'error', 3000);
    }
  };

  const handleSave = async () => {
    if (!formData.section || !formData.graveRow || !formData.sequentialNumber) {
      notify('Prosim izpolnite vsa obvezna polja (Odsek, Vrsta, Zaporedna številka)', 'warning', 3000);
      return;
    }

    setIsSaving(true);
    try {
      if (isNew) {
        await graveSitesApi.create(formData, cemeterySlug);
        notify('Grobno mesto je bilo uspešno dodano', 'success', 3000);
      } else {
        await graveSitesApi.update(data.id, formData, cemeterySlug);
        notify('Grobno mesto je bilo uspešno posodobljeno', 'success', 3000);
      }
      onSaved();
      onHiding();
    } catch (error: any) {
      console.error('Failed to save grave site:', error);
      const errorMsg = error.response?.data?.error || 'Napaka pri shranjevanju';
      notify(errorMsg, 'error', 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onHiding();
  };

  // Deceased person form handlers
  const handleSaveDeceased = async () => {
    if (!deceasedFormData.firstName || !deceasedFormData.lastName) {
      notify('Prosim izpolnite obvezna polja (Ime, Priimek)', 'warning', 3000);
      return;
    }

    setIsSavingDeceased(true);
    try {
      // Convert empty strings to null for optional fields before sending to API
      const cleanedData = {
        ...deceasedFormData,
        middleName: deceasedFormData.middleName || null,
        maidenName: deceasedFormData.maidenName || null,
        graveType: deceasedFormData.graveType || null,
        contractNumber: deceasedFormData.contractNumber || null,
        notes: deceasedFormData.notes || null,
        birthYear: deceasedFormData.birthYear || null,
        deathYear: deceasedFormData.deathYear || null
      };

      console.log('Saving deceased data:', cleanedData);
      
      // Create FormData as expected by DevExtreme backend
      const requestForm = new FormData();
      
      if (editingDeceasedId) {
        // Update existing deceased person
        requestForm.append('key', editingDeceasedId.toString());
        requestForm.append('values', JSON.stringify(cleanedData));
        
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068/api';
        const response = await fetch(`${baseUrl}/deceased?cemeterySlug=${encodeURIComponent(cemeterySlug ?? '')}`, {
          method: 'PUT',
          credentials: 'include',
          body: requestForm
        });
        
        if (!response.ok) {
          throw new Error(`Update failed: ${response.statusText}`);
        }
        
        notify('Pokojni je bil uspešno posodobljen', 'success', 2000);
      } else {
        // Create new deceased person - ensure graveSiteId is included
        const dataWithGraveSite = {
          ...cleanedData,
          graveSiteId: formData.id
        };
        requestForm.append('values', JSON.stringify(dataWithGraveSite));
        
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068/api';
        const response = await fetch(`${baseUrl}/deceased?cemeterySlug=${encodeURIComponent(cemeterySlug ?? '')}`, {
          method: 'POST',
          credentials: 'include',
          body: requestForm
        });
        
        if (!response.ok) {
          throw new Error(`Create failed: ${response.statusText}`);
        }
        
        notify('Pokojni je bil uspešno dodan', 'success', 2000);
      }
      
      setDeceasedPopupVisible(false);
      // Note: form cleanup is handled by useEffect when deceasedPopupVisible changes
    } catch (error: any) {
      console.error('Failed to save deceased:', error);
      notify('Napaka pri shranjevanju pokojnega', 'error', 3000);
    } finally {
      setIsSavingDeceased(false);
    }
  };

  const handleCloseDeceasedPopup = () => {
    setDeceasedPopupVisible(false);
    // Note: form data cleanup is handled by useEffect when deceasedPopupVisible changes
  };

  // Render basic data tab
  const renderBasicDataTab = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Section */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Odsek <span style={{ color: 'red' }}>*</span>
          </label>
          <TextBox
            value={formData.section || ''}
            onValueChanged={(e) => updateFormData((prev: any) => ({ ...prev, section: e.value }))}
            placeholder="Vnesite odsek"
            stylingMode="outlined"
          />
        </div>

        {/* Grave Row */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Vrsta <span style={{ color: 'red' }}>*</span>
          </label>
          <NumberBox
            value={formData.graveRow !== undefined && formData.graveRow !== null ? formData.graveRow : (initialGraveRow || null)}
            onValueChanged={(e) => {
              // Prevent any null changes when we have a valid initial value
              if (e.value === null && initialGraveRow !== null && initialGraveRow !== undefined) {
                // Force the value back to the initial value
                updateFormData((prevData: any) => ({ ...prevData, graveRow: initialGraveRow }));
                return;
              }
              updateFormData((prev: any) => ({ ...prev, graveRow: e.value }));
            }}
            placeholder="Vnesite vrsto"
            stylingMode="outlined"
          />
        </div>

        {/* Sequential Number */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Zaporedna številka <span style={{ color: 'red' }}>*</span>
          </label>
          <TextBox
            value={formData.sequentialNumber || ''}
            onValueChanged={(e) => updateFormData((prev: any) => ({ ...prev, sequentialNumber: e.value }))}
            placeholder="Vnesite zaporedno številko"
            stylingMode="outlined"
          />
        </div>

        {/* Category with Autocomplete */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Kategorija
          </label>
          <Autocomplete
            value={formData.category || ''}
            dataSource={categories}
            onValueChanged={(e) => updateFormData((prev: any) => ({ ...prev, category: e.value }))}
            placeholder="Vnesite ali izberite kategorijo"
            minSearchLength={0}
            searchTimeout={200}
            showClearButton={true}
            searchMode="startswith"
            stylingMode="outlined"
          />
        </div>

        {/* Department */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Oddelek
          </label>
          <TextBox
            value={formData.department || ''}
            onValueChanged={(e) => updateFormData((prev: any) => ({ ...prev, department: e.value }))}
            placeholder="Vnesite oddelek"
            stylingMode="outlined"
          />
        </div>

        {/* Active Status - full width */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Status
          </label>
          <CheckBox
            value={formData.isActive ?? true}
            onValueChanged={(e: any) => updateFormData((prev: any) => ({ ...prev, isActive: e.value }))}
            text="Grob je aktiven"
          />
        </div>
      </div>
    </div>
  );

  // Render deceased tab with custom management
  const renderDeceasedTab = () => (
    <div style={{ padding: '20px' }}>
      {!data?.id ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>Najprej shranite osnovne podatke groba, da lahko dodate pokojne osebe.</p>
        </div>
      ) : (
        <>
          {/* Simple DataGrid for viewing deceased persons */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
              <Button
                text="Dodaj pokojnega"
                type="default"
                icon="add"
                onClick={() => {
                  setDeceasedFormData(getDefaultDeceasedFormData());
                  setEditingDeceasedId(null);
                  setDeceasedPopupVisible(true);
                }}
              />
            </div>
            
            <DataGrid
              dataSource={graveSitesApi.getDeceasedByGrave(data.id, cemeterySlug)}
              showBorders={true}
              showRowLines={true}
              rowAlternationEnabled={true}
              height={'50vh'}
              remoteOperations={true}
              columnAutoWidth={true}
            >
              <Paging enabled={true} defaultPageSize={10} />
              
              {/* Display columns only */}
              <Column dataField="fullName" caption="Polno ime" minWidth={180} allowSorting={false} />
              <Column dataField="displayBirthDate" caption="Rojstvo" minWidth={100} allowSorting={false} />
              <Column dataField="displayDeathDate" caption="Smrt" minWidth={100} allowSorting={false} />
              <Column dataField="age" caption="Starost" minWidth={80} allowSorting={false} />
              <Column dataField="graveType" caption="Tip groba" minWidth={100} allowSorting={false} />
              <Column dataField="allowPublication" caption="Objava dovoljena" dataType="boolean" minWidth={120} allowSorting={false} />
              
              {/* Command buttons column */}
              <Column 
                type="buttons" 
                fixed={true}
                fixedPosition="right"
                width={120}
                buttons={[
                  {
                    //name: 'edit1',
                    hint: 'Uredi',
                    icon: 'edit',
                    onClick: (e: any) => {
                      // Load only the actual form fields for editing, not computed display fields
                      // CRITICAL: Convert date strings to Date objects and handle null/empty values properly
                      const editData = {
                        graveSiteId: e.row.data.graveSiteId,
                        firstName: e.row.data.firstName || '',
                        lastName: e.row.data.lastName || '',
                        middleName: e.row.data.middleName || '',
                        maidenName: e.row.data.maidenName || '',
                        birthDate: e.row.data.birthDate ? new Date(e.row.data.birthDate) : null,
                        deathDate: e.row.data.deathDate ? new Date(e.row.data.deathDate) : null,
                        burialDate: e.row.data.burialDate ? new Date(e.row.data.burialDate) : null,
                        graveType: e.row.data.graveType || '',
                        birthYear: e.row.data.birthYear || null,
                        deathYear: e.row.data.deathYear || null,
                        contractNumber: e.row.data.contractNumber || '',
                        notes: e.row.data.notes || '',
                        allowPublication: e.row.data.allowPublication ?? true
                      };
                      console.log('Loading deceased for edit:', editData);
                      setDeceasedFormData(editData);
                      setEditingDeceasedId(e.row.data.id);
                      setDeceasedPopupVisible(true);
                    }
                  },
                  {
                    //name: 'delete1',
                    hint: 'Izbriši',
                    icon: 'trash',
                    onClick: async (e: any) => {
                      const confirmed = await confirm('Ali ste prepričani, da želite izbrisati ta zapis?', 'Potrditev');
                      if (confirmed) {
                        try {
                          // Use FormData as expected by DevExtreme backend
                          const requestForm = new FormData();
                          requestForm.append('key', e.row.data.id.toString());
                          
                          const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5068/api';
                          const response = await fetch(`${baseUrl}/deceased?cemeterySlug=${encodeURIComponent(cemeterySlug ?? '')}`, {
                            method: 'DELETE',
                            credentials: 'include',
                            body: requestForm
                          });
                          
                          if (!response.ok) {
                            throw new Error(`Delete failed: ${response.statusText}`);
                          }
                          
                          notify('Pokojni je bil uspešno izbrisan', 'success', 2000);
                          // Refresh the DataGrid
                          e.component.refresh();
                        } catch (error) {
                          console.error('Failed to delete deceased:', error);
                          notify('Napaka pri brisanju pokojnega', 'error', 3000);
                        }
                      }
                    }
                  }
                ]}
              />
            </DataGrid>
          </div>
        </>
      )}
    </div>
  );

  // Render location tab
  const renderLocationTab = () => (
    <div style={{ padding: '20px', height: '70vh', display: 'flex', flexDirection: 'column' }}>
      {!data?.id ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>Najprej shranite osnovne podatke groba, da lahko nastavite lokacijo.</p>
        </div>
      ) : !cemeteryData || !cemeteryData.photoPath ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>Fotografija pokopališča ni na voljo. Naložite fotografijo v zavihku "Podatki pokopališča".</p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <p style={{ margin: 0, color: '#666', flex: 1 }}>
              Kliknite na zemljevid, da nastavite GPS koordinate groba
              {locationMarker && (
                <span style={{ display: 'block', fontSize: '12px', marginTop: '5px', color: '#2196F3' }}>
                  📍 Lat: {locationMarker.lat.toFixed(6)}, Lng: {locationMarker.lng.toFixed(6)}
                </span>
              )}
            </p>
            <Button
              text="Shrani lokacijo"
              type="success"
              icon="save"
              onClick={handleSaveLocation}
              disabled={isSavingLocation || !locationMarker}
            />
            <Button
              text="Počisti"
              type="normal"
              icon="clear"
              onClick={handleClearLocation}
              disabled={!locationMarker}
            />
          </div>
          
          <div style={{ flex: 1, border: '2px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
            <MapContainer
              key={cemeteryData.cemeterySlug}
              center={locationMarker
                ? [locationMarker.lat, locationMarker.lng]
                : cemeteryData.centerLat != null && cemeteryData.centerLng != null
                  ? [cemeteryData.centerLat, cemeteryData.centerLng]
                  : CEMETERY_BOUNDS.center}
              zoom={cemeteryData.defaultZoom ?? CEMETERY_BOUNDS.zoom}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
              maxZoom={22}
            >
              {/* Base Layer: Esri World Imagery (free satellite imagery) */}
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={20}
                minZoom={3}
              />
              
              {/* ALTERNATIVE: OpenStreetMap (street map, not satellite) */}
              {/* <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
                minZoom={3}
              /> */}
              
              {/* BACKUP: Sentinel-2 Cloudless (lower resolution, kept for fallback) */}
              {/* <TileLayer
                attribution='<a href="https://s2maps.eu" target="_blank">Sentinel-2 cloudless</a> by <a href="https://eox.at" target="_blank">EOX IT Services</a>'
                url="https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2020_3857/default/GoogleMapsCompatible/{z}/{y}/{x}.jpg"
                maxZoom={22}
                maxNativeZoom={18}
                minZoom={3}
              /> */}
              
              {/* Drone Photo Overlay */}
              <DistortableImageLayer
                imageUrl={`${import.meta.env.VITE_API_BASE_URL?.startsWith('http') ? import.meta.env.VITE_API_BASE_URL.replace('/api', '') : window.location.origin}${cemeteryData.photoPath}`}
                placement={getOverlayPlacement(cemeteryData)}
                editable={false}
                opacity={1}
              />
              
              {/* Click handler to set location */}
              <MapClickHandler onLocationSet={(lat, lng) => setLocationMarker({ lat, lng })} />
              
              {/* Marker at selected location */}
              {locationMarker && (
                <Marker position={[locationMarker.lat, locationMarker.lng]} />
              )}
            </MapContainer>
          </div>
        </>
      )}
    </div>
  );

  // Render photo tab
  const renderPhotoTab = () => (
    <div style={{ padding: '20px' }}>
      {!data?.id ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>Najprej shranite osnovne podatke groba, da lahko naložite fotografijo.</p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '10px' }}>Naložite fotografijo groba</h3>
            <FileUploader
              selectButtonText="Izberite sliko"
              labelText="ali povlecite sliko sem"
              accept="image/jpeg,image/png,image/gif,image/bmp"
              uploadMode="useForm"
              maxFileSize={10485760}
              onValueChanged={handlePhotoUpload}
              disabled={isUploadingPhoto}
              allowedFileExtensions={['.jpg', '.jpeg', '.png', '.gif', '.bmp']}
              multiple={false}
              showFileList={true}
            />
            <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
              Največja velikost: 10MB. Dovoljene vrste: JPG, PNG, GIF, BMP
            </p>
          </div>
          
          {currentPhoto && (
            <div style={{ marginTop: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Trenutna fotografija</h3>
                <Button
                  text="Izbriši fotografijo"
                  type="danger"
                  icon="trash"
                  onClick={handleDeletePhoto}
                />
              </div>
              <div style={{ border: '2px solid #ddd', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                <img
                  src={currentPhoto}
                  alt="Grave site"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  // Render deceased person edit form
  const renderDeceasedForm = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* First Name with Autocomplete */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Ime <span style={{ color: 'red' }}>*</span>
          </label>
          <Autocomplete
            value={deceasedFormData.firstName || ''}
            dataSource={firstNames}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, firstName: e.value })}
            placeholder="Vnesite ime"
            minSearchLength={0}
            searchTimeout={200}
            showClearButton={true}
            searchMode="startswith"
            stylingMode="outlined"
          />
        </div>

        {/* Last Name with Autocomplete */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Priimek <span style={{ color: 'red' }}>*</span>
          </label>
          <Autocomplete
            value={deceasedFormData.lastName || ''}
            dataSource={lastNames}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, lastName: e.value })}
            placeholder="Vnesite priimek"
            minSearchLength={0}
            searchTimeout={200}
            showClearButton={true}
            searchMode="startswith"
            stylingMode="outlined"
          />
        </div>

        {/* Middle Name with Autocomplete */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Srednje ime
          </label>
          <Autocomplete
            value={deceasedFormData.middleName || ''}
            dataSource={middleNames}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, middleName: e.value || null })}
            placeholder="Vnesite srednje ime"
            minSearchLength={0}
            searchTimeout={200}
            showClearButton={true}
            searchMode="startswith"
            stylingMode="outlined"
            acceptCustomValue={true}
          />
        </div>

        {/* Maiden Name with Autocomplete */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Dekliški priimek
          </label>
          <Autocomplete
            value={deceasedFormData.maidenName || ''}
            dataSource={maidenNames}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, maidenName: e.value || null })}
            placeholder="Vnesite dekliški priimek"
            minSearchLength={0}
            searchTimeout={200}
            showClearButton={true}
            searchMode="startswith"
            stylingMode="outlined"
            acceptCustomValue={true}
          />
        </div>

        {/* Birth Date */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Datum rojstva
          </label>
          <DateBox
            value={deceasedFormData.birthDate || null}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, birthDate: e.value })}
            displayFormat="dd.MM.yyyy"
            placeholder="Izberite datum rojstva"
            stylingMode="outlined"
            showClearButton={true}
          />
        </div>

        {/* Death Date */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Datum smrti
          </label>
          <DateBox
            value={deceasedFormData.deathDate || null}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, deathDate: e.value })}
            displayFormat="dd.MM.yyyy"
            placeholder="Izberite datum smrti"
            stylingMode="outlined"
            showClearButton={true}
          />
        </div>

        {/* Burial Date */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Datum pokopa
          </label>
          <DateBox
            value={deceasedFormData.burialDate || null}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, burialDate: e.value })}
            displayFormat="dd.MM.yyyy"
            placeholder="Izberite datum pokopa"
            stylingMode="outlined"
            showClearButton={true}
          />
        </div>

        {/* Grave Type with Autocomplete */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Tip groba
          </label>
          <Autocomplete
            value={deceasedFormData.graveType || ''}
            dataSource={graveTypes}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, graveType: e.value || null })}
            placeholder="Vnesite tip groba"
            minSearchLength={0}
            searchTimeout={200}
            showClearButton={true}
            searchMode="startswith"
            stylingMode="outlined"
            acceptCustomValue={true}
          />
        </div>

        {/* Birth Year */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Leto rojstva
          </label>
          <NumberBox
            value={deceasedFormData.birthYear || null}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, birthYear: e.value })}
            placeholder="Vnesite leto rojstva"
            stylingMode="outlined"
            showClearButton={true}
          />
        </div>

        {/* Death Year */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Leto smrti
          </label>
          <NumberBox
            value={deceasedFormData.deathYear || null}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, deathYear: e.value })}
            placeholder="Vnesite leto smrti"
            stylingMode="outlined"
            showClearButton={true}
          />
        </div>

        {/* Contract Number */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Številka pogodbe
          </label>
          <TextBox
            value={deceasedFormData.contractNumber || ''}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, contractNumber: e.value || null })}
            placeholder="Vnesite številko pogodbe"
            stylingMode="outlined"
          />
        </div>

        {/* Notes - full width */}
        <div style={{ gridColumn: 'span 2' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Opombe
          </label>
          <TextBox
            value={deceasedFormData.notes || ''}
            onValueChanged={(e) => setDeceasedFormData({ ...deceasedFormData, notes: e.value || null })}
            placeholder="Vnesite opombe"
            stylingMode="outlined"
          />
        </div>

        {/* Allow Publication - full width */}
        <div style={{ gridColumn: 'span 2' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Objava
          </label>
          <CheckBox
            value={deceasedFormData.allowPublication ?? true}
            onValueChanged={(e: any) => setDeceasedFormData({ ...deceasedFormData, allowPublication: e.value })}
            text="Dovoli objavo podatkov"
          />
        </div>
      </div>
    </div>
  );

  const tabItems = [
    { id: 0, text: 'Grob', icon: 'info' },
    { id: 1, text: 'Pokojni', icon: 'card' },
    { id: 2, text: 'Lokacija', icon: 'pinmap' },
    { id: 3, text: 'Fotografija', icon: 'image' }
  ];

  const renderCurrentTab = () => {
    switch (selectedTabIndex) {
      case 0:
        return renderBasicDataTab();
      case 1:
        return renderDeceasedTab();
      case 2:
        return renderLocationTab();
      case 3:
        return renderPhotoTab();
      default:
        return null;
    }
  };

  return (
    <>
      <Popup
        visible={visible && data !== null}
        onHiding={onHiding}
        dragEnabled={false}
        hideOnOutsideClick={false}
        showTitle={true}
        title={isNew ? 'Dodaj novo grobno mesto' : `Urejanje: ${data?.graveCode || ''}`}
        width="90vw"
        height="90vh"
        showCloseButton={true}
        contentRender={() => (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Tabs
              dataSource={tabItems}
              selectedIndex={selectedTabIndex}
              onSelectionChanged={(e) => {
                if (e.addedItems && e.addedItems.length > 0) {
                  setSelectedTabIndex(e.addedItems[0].id);
                }
              }}
              width="100%"
            />
            <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
              {renderCurrentTab()}
            </div>
            {/* Bottom toolbar with Save and Close buttons */}
            <div style={{ 
              padding: '15px 20px', 
              borderTop: '1px solid #ddd', 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '10px',
              backgroundColor: '#f8f9fa'
            }}>
              <Button
                text="Zapri"
                type="normal"
                stylingMode="outlined"
                onClick={handleClose}
                icon="close"
              />
              <Button
                text="Shrani"
                type="default"
                stylingMode="contained"
                onClick={handleSave}
                disabled={isSaving}
                icon="save"
              />
            </div>
          </div>
        )}
      />

      {/* Deceased Person Edit Popup */}
      <Popup
        key={editingDeceasedId || 'new'} 
        visible={deceasedPopupVisible}
        onHiding={handleCloseDeceasedPopup}
        dragEnabled={false}
        hideOnOutsideClick={false}
        showTitle={true}
        title={editingDeceasedId ? 'Urejanje pokojnega' : 'Dodaj pokojnega'}
        width="800px"
        height="600px"
        showCloseButton={true}
        contentRender={() => (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
              {renderDeceasedForm()}
            </div>
            {/* Bottom toolbar with Save and Close buttons */}
            <div style={{ 
              padding: '15px 20px', 
              borderTop: '1px solid #ddd', 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '10px',
              backgroundColor: '#f8f9fa'
            }}>
              <Button
                text="Prekliči"
                type="normal"
                stylingMode="outlined"
                onClick={handleCloseDeceasedPopup}
                icon="close"
              />
              <Button
                text="Shrani"
                type="default"
                stylingMode="contained"
                onClick={handleSaveDeceased}
                disabled={isSavingDeceased}
                icon="save"
              />
            </div>
          </div>
        )}
      />
    </>
  );
};

export default GraveSitePopupEditor;
